import React from "react";
import { useState } from "react";
import { useEffect } from "react";
import client from "helpers/sanity";
import { useParams } from "react-router-dom";
import { ColorButton } from "components/diet";
import Ratings from "components/diet/Ratings";
import imageBuilder from "helpers/imageBuilder";
import RichText from "components/diet/RichText";
import ContentLockPopup from "components/ContentLock";
import { categoriesQuery, singleDietQuery, tagsQuery } from "constants/queries";
import { useDispatch, useSelector } from "react-redux";
import { addSubscription, getUser } from "helpers/firebase/userService";
import { setUser } from "../../redux/userSlice";
import NoResult from "components/NoResult";
import Loader from "components/Loader";

const BlogDetail = () => {
  const [tags, setTags] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [isPopupOpen, setIsPopupOpen] = useState(true);

  const params = useParams();
  const dispatch = useDispatch();

  const { id } = params;

  const userId = useSelector((state) => state.user.userId);

  useEffect(() => {
    async function checkUserSubscription() {
      const user = await getUser(userId);
      if (user) {
        const hasSubscription = user.subscriptions.find(
          (subscriptionId) => subscriptionId === id
        );

        if (user.role === "admin" || hasSubscription) {
          setIsPopupOpen(false);
        }
      }

      setTimeout(() => {
        setLoading(false);
      }, 0);
    }

    checkUserSubscription();
  }, [userId, id]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dietData, categoriesData, tagsData] = await Promise.all([
          client.fetch(singleDietQuery, { id }),
          client.fetch(categoriesQuery()),
          client.fetch(tagsQuery),
        ]);

        setData(dietData);
        setCategories(categoriesData);
        setTags(tagsData);
      } catch (err) {
        console.error(err.message);
      }
    };

    fetchData();
  }, [id]);

  const closePopup = () => setIsPopupOpen(false);

  const handleSubscription = async () => {
    try {
      await addSubscription(userId, id);
      const updatedUser = await getUser(userId);
      console.log(updatedUser);
      dispatch(setUser(updatedUser));
      closePopup();
    } catch (err) {
      console.log(err);
    }
  };

  if (loading)
    return (
      <div className="h-[100dvh] w-screen">
        <Loader />
      </div>
    );

  if (!data && !loading) return <NoResult />;

  const imageUrl = imageBuilder(data.featuredImage, 1200, 800);
  const authProfileImage = imageBuilder(data.author?.profilePic, 52, 52);

  return (
    <main className="w-full flex flex-col md:flex-row max-w-screen-xl mx-auto my-4 sm:my-6 md:my-8 px-1 sm:px-2 md:px-4 gap-4">
      <ContentLockPopup
        price={data?.price}
        isOpen={isPopupOpen}
        closePopup={closePopup}
        handleSuccess={handleSubscription}
      />
      {!isPopupOpen ? (
        <>
          {/* Main Section */}
          <section className="w-full lg:w-2/3">
            {/* Blog Title and Meta Details */}
            <div className="mb-2 sm:mb-4 md:mb-6">
              <h1 className="text-xl sm:text-2xl md:text-4xl font-bold mb-2 capitalize">
                {data.title.toLowerCase()}
              </h1>
              <p className="text-gray-600 text-[12px] sm:text-[14px] md:text-[16px] mb-4 capitalize">
                {data.desc.toLowerCase()}
              </p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center text-sm text-gray-500 space-y-2 sm:space-y-0 sm:space-x-2 md:space-x-4">
                <div className="flex items-center justify-center">
                  {authProfileImage && (
                    <img
                      className="h-6 w-6 rounded-full mr-2"
                      src={authProfileImage}
                      alt={data.author?.name}
                    />
                  )}
                  <span className="text-[12px] sm:text-[14px] md:text-[16px] capitalize">
                    By {data.author?.name.toLowerCase()}
                  </span>
                </div>
                <Ratings rating={data.rating} />
                <div className="flex items-center justify-between w-full">
                  <span className="text-[12px] sm:text-[14px] md:text-[16px]">
                    Method:
                    <span className="text-[12px] sm:text-[14px] md:text-[16px] capitalize">
                      {data.methodDuration?.method}
                    </span>
                  </span>
                  <span className="text-[12px] sm:text-[14px] md:text-[16px]">
                    Duration: {data.methodDuration?.duration} Minutes
                  </span>
                </div>
              </div>
            </div>

            {/* Featured Image */}
            {imageUrl && (
              <div className="mb-2 sm:mb-4 md:mb-6">
                <img
                  src={imageUrl}
                  alt={data.title}
                  className="w-full h-auto max-h-[30rem] rounded-xl object-cover"
                />
              </div>
            )}

            {/* Rich Content */}
            <RichText content={data?.content} />

            {/* YouTube Video */}
            {data?.videoUrl && (
              <div className="aspect-video">
                <iframe
                  src={data?.videoUrl}
                  title="YouTube Video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>
            )}
          </section>

          {/* Sidebar */}
          <aside className="w-full lg:w-1/3 bg-gray-100 p-4 rounded-md">
            {/* Categories */}
            <div className="mb-6">
              <h2 className="text-sm sm:text-md md:text-lg font-semibold mb-3">
                Categories
              </h2>
              <div className="space-y-1 sm:space-y-2 space-x-1">
                {categories?.map((category, index) => (
                  <ColorButton
                    key={index}
                    title={category.title}
                    colorIndex={index}
                  />
                ))}
              </div>
            </div>

            {/* Tags */}
            <div>
              <h2 className="text-sm sm:text-md md:text-lg font-semibold mb-3">
                Tags
              </h2>
              <div className="flex flex-wrap gap-2">
                {tags?.map((tag, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-gray-200 text-[10px] sm:text-[12px] md:text-sm rounded-full text-gray-700 capitalize"
                  >
                    #{tag?.title}
                  </span>
                ))}
              </div>
            </div>
          </aside>
        </>
      ) : <div className="h-[100dvh] w-screen"></div>}
    </main>
  );
};

export default BlogDetail;
